import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearResidenceCache } from '../utils/residenceCache';
import { supabase } from '../lib/supabase';
import type { User } from '../types/user';

// Define the auth context type
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  adminResolved: boolean; // true når isAdmin-feltet er hydrert fra DB
  // eslint-disable-next-line no-unused-vars
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUserData: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
  forceRefreshUserData: () => Promise<void>;
  loadFullUserData: () => Promise<User | null>;
  // eslint-disable-next-line no-unused-vars
  resetPassword: (email: string) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  updatePassword: (newPassword: string) => Promise<void>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);


// Stripped: ingen global cache/locks/debug

// Hjelpefunksjon for å sjekke cached session synkront ved oppstart
function getOptimisticAuthState(): { user: User | null; isAuthenticated: boolean; loading: boolean } {
  try {
    const cachedSession = localStorage.getItem('condo_auth_token');
    
    if (cachedSession) {
      const parsed = JSON.parse(cachedSession);
      
      // Supabase v2 kan lagre som { session: { user, expires_at } } eller { user, expires_at }
      const session = parsed?.session || parsed;
      const user = session?.user;
      const refreshToken = session?.refresh_token || parsed?.refresh_token;
      
      if (user?.id && user?.email) {
        // Hvis vi har refresh_token, er bruker fortsatt "logget inn"
        // Token refresh skjer i bakgrunnen via Supabase autoRefreshToken
        const hasRefreshToken = !!refreshToken;
        
        console.log('[AuthContext] Optimistisk auth sjekk:', { 
          userId: user.id, 
          hasRefreshToken,
          email: user.email?.substring(0, 3) + '***'
        });
        
        if (hasRefreshToken) {
          console.log('[AuthContext] ✅ Optimistisk auth aktivert (har refresh token)');
          return {
            user: {
              id: user.id,
              email: user.email,
              name: user.email?.split('@')[0] || 'Bruker',
              apartment: '',
              isAdmin: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            isAuthenticated: true,
            loading: false
          };
        }
      }
    }
  } catch (err) {
    console.log('[AuthContext] ❌ Kunne ikke parse cached session', err);
  }
  return { user: null, isAuthenticated: false, loading: true };
}

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Optimistisk auth: sjekk localStorage SYNKRONT ved mount
  const optimisticState = getOptimisticAuthState();
  
  const [user, setUser] = useState<User | null>(optimisticState.user);
  const [loading, setLoading] = useState(optimisticState.loading);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(optimisticState.isAuthenticated);
  const [adminResolved, setAdminResolved] = useState(false);
  const queryClient = useQueryClient();

  // Stripped: ingen prefetch

  // Stripped: ingen window-eksponering

  // Funksjon for å hente brukerdata
  const fetchUserData = useCallback(async (userId: string, userEmail?: string): Promise<User> => {
    // Valider userId
    if (!userId || typeof userId !== 'string' || userId.length !== 36) {
      throw new Error('Ugyldig bruker-ID');
    }

    let email = userEmail;

    // Hvis email ikke er gitt som parameter, hent fra session
    if (!email) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Ingen aktiv session');
      }
      email = session.user.email;
    }

    // Rask short-circuit
    const { count: belongCount } = await supabase
      .from('user_belong')
      .select('id', { count: 'exact', head: true })
      .eq('profiles_id', userId);

    if ((belongCount ?? 0) === 0) {
      return {
        id: userId,
        email: email || userEmail || '',
        name: (email || userEmail || '').split('@')[0] || 'Bruker',
        apartment: '',
        isAdmin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    const { data: userDataArray, error: userDataError } = await supabase
      .from('user_data_view')
      .select('*')
      .eq('profiles_id', userId);
    if (userDataError) {
      throw new Error(`Databasefeil: ${userDataError.message}`);
    }
    if (!userDataArray || userDataArray.length === 0) {
      return {
        id: userId,
        email: email || userEmail || '',
        name: (email || userEmail || '').split('@')[0] || 'Bruker',
        apartment: '',
        isAdmin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    return {
      id: userId,
      email: userEmail || '',
      name: `${userDataArray[0].first_name || ''} ${userDataArray[0].last_name || ''}`.trim() || 'Bruker',
      apartment: '',
      isAdmin: Boolean(userDataArray[0].is_admin),
      created_at: new Date().toISOString(),
      updated_at: userDataArray[0].updated_at || new Date().toISOString()
    };
  }, []);

  // Funksjon for å oppdatere brukerdata
  const refreshUserData = useCallback(async () => {
    if (!user?.id) return;
    const userData = await fetchUserData(user.id);
    setUser(userData);
    setAdminResolved(true);
  }, [user?.id, fetchUserData]);

  // Funksjon for å oppdatere admin-status
  const refreshAdminStatus = useCallback(async () => {
    if (!user?.id) return;
    const userData = await fetchUserData(user.id);
    setUser(userData);
    setAdminResolved(true);
  }, [user?.id, fetchUserData]);

  // Funksjon for å tvinge ny henting av brukerdata (ignorerer cache)
  const forceRefreshUserData = useCallback(async () => {
    if (!user?.id) return;
    const userData = await fetchUserData(user.id);
    setUser(userData);
    setAdminResolved(true);
  }, [user?.id, fetchUserData]);

  // Funksjon for komponenter å hente full brukerdata når de trenger det
  const loadFullUserData = useCallback(async () => {
    if (!user?.id) return null;
    try {
      const fullUserData = await fetchUserData(user.id, user.email);
      setUser(fullUserData);
      setAdminResolved(true);
      return fullUserData;
    } catch {
      return null;
    }
  }, [user?.id, user?.email, fetchUserData]);

  // Stripped: ingen periodisk state-logging

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[AuthContext login] start', { emailMasked: email.replace(/(^.).*(@.*$)/, '$1***$2') });
      // Fjern pre-getSession/signOut for å unngå heng; Supabase håndterer sesjonsbytte ved signIn
      // Timeout-guard i tilfelle heng – øk litt og fjern timer-konflikter
      const timeoutMs = 15000;
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Innlogging tok for lang tid (timeout)')), timeoutMs));
      let data: any; let error: any;
      try {
        console.time('[AuthContext login] signInWithPassword');
        ({ data, error } = await Promise.race([
          supabase.auth.signInWithPassword({ email, password }),
          timeoutPromise
        ]) as { data: any; error: any });
      } finally {
        // Sørg for at console.time alltid matches
        console.timeEnd('[AuthContext login] signInWithPassword');
      }
      if (error) {
        console.log('[AuthContext login] auth error details', { code: (error as any)?.code, status: (error as any)?.status, message: (error as any)?.message });
        throw error;
      }
      if (!data.session) throw new Error('Ingen sesjon generert');
      try {
        localStorage.removeItem('currentResidence');
      } catch {
        // ignorer
      }
      const minimalUser: User = {
        id: data.session.user.id,
        email: data.session.user.email || '',
        name: data.session.user.email?.split('@')[0] || 'Bruker',
        apartment: '',
        isAdmin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUser(minimalUser);
      setIsAuthenticated(true);
      setLoading(false);
      console.log('[AuthContext login] success', { userId: minimalUser.id });
      return minimalUser;
    } catch (err) {
      console.log('[AuthContext login] error', err);
      // Hvis vi fikk timeout under login, sjekk om session faktisk finnes og behandle som suksess
      try {
        // Prøv en kort retry-loop (inntil 3 forsøk, 150 ms mellom) for å fange session som nettopp ble etablert
        let post: any = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          const res = await supabase.auth.getSession();
          post = res?.data;
          if (post?.session) break;
          await new Promise(r => setTimeout(r, 150));
        }
        if (post?.session) {
          const minimalUser: User = {
            id: post.session.user.id,
            email: post.session.user.email || '',
            name: post.session.user.email?.split('@')[0] || 'Bruker',
            apartment: '',
            isAdmin: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setUser(minimalUser);
          setIsAuthenticated(true);
          setLoading(false);
          console.log('[AuthContext login] recovered via existing session (retry)', { userId: minimalUser.id });
          return minimalUser;
        }
      } catch {
        // Ignorer feil ved retry
      }

      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('En ukjent feil oppstod ved innlogging');
      }
      setLoading(false);
      throw err;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Hard reset før vi roper signOut
      setUser(null);
      setIsAuthenticated(false);
      try {
        localStorage.removeItem('currentResidence');
        localStorage.removeItem('condo_auth_token');
      } catch {
        // ignorer
      }
      try {
        clearResidenceCache();
      } catch {
        // ignorer
      }
      try {
        queryClient.clear();
      } catch {
        // ignorer
      }
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      window.location.href = '/landing';
    } catch {
      setError('En feil oppstod ved utlogging');
      window.location.href = '/landing';
    }
  };

  // Clear error function
  const clearError = () => setError(null);

  // Reset password function - sender e-post med reset-lenke
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (resetError) {
        throw resetError;
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('En feil oppstod ved sending av passord-reset e-post');
      }
      throw err;
    }
  };

  // Update password function - oppdaterer passordet etter reset
  const updatePassword = async (newPassword: string) => {
    try {
      setError(null);
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (updateError) {
        throw updateError;
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('En feil oppstod ved oppdatering av passord');
      }
      throw err;
    }
  };

  useEffect(() => {
    let mounted = true;
    let isInitialLoad = true;
    let hydrationGraceTimer: number | undefined;
    let initialEventHandled = false;
    const hasOptimisticSession = optimisticState.isAuthenticated;
    console.log('[AuthContext mount]', { hasOptimisticSession });

    // HARD TIMEOUT: Sikrer at loading aldri henger mer enn 12 sekunder
    // Kun relevant hvis vi ikke har optimistisk session
    const hardTimeoutTimer = !hasOptimisticSession ? window.setTimeout(() => {
      if (mounted) {
        console.warn('[AuthContext] HARD TIMEOUT reached (12s) - forcing loading = false');
        setLoading(false);
      }
    }, 12000) : undefined;

    // Valider session i bakgrunnen (blokkerer ikke UI hvis vi har optimistisk session)
    (async () => {
      try {
        const startTime = Date.now();
        const { data: { session } } = await supabase.auth.getSession();
        const duration = Date.now() - startTime;
        console.log('[AuthContext getSession]', { hasSession: Boolean(session), durationMs: duration, hasOptimisticSession });
        if (!mounted) return;
        
        if (session) {
          // Oppdater med faktisk session-data (kan være forskjellig fra optimistisk)
          try {
            const userData = await fetchUserData(session.user.id, session.user.email || '');
            if (!mounted) return;
            userData.email = session.user.email || '';
            setUser(userData);
            setIsAuthenticated(true);
            setAdminResolved(true);
            console.log('[AuthContext getSession] user set', { userId: userData.id });
            if (mounted) setLoading(false);
            isInitialLoad = false;
            // Rydd opp hard timeout timer hvis den finnes
            if (hardTimeoutTimer) {
              clearTimeout(hardTimeoutTimer);
            }
          } catch (err) {
            if (!mounted) return;
            console.error('[AuthContext getSession] fetchUserData failed', err);
            setError('Kunne ikke hente brukerdata');
            if (mounted) setLoading(false);
            isInitialLoad = false;
            // Rydd opp hard timeout timer hvis den finnes
            if (hardTimeoutTimer) {
              clearTimeout(hardTimeoutTimer);
            }
          }
        } else {
          // Ingen session - nullstill optimistisk state hvis vi hadde det
          if (hasOptimisticSession) {
            console.log('[AuthContext] Optimistisk session ugyldig - nullstiller');
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            isInitialLoad = false;
            // Rydd opp hard timeout timer hvis den finnes
            if (hardTimeoutTimer) {
              clearTimeout(hardTimeoutTimer);
            }
          } else {
            // Grace timer for ikke-optimistisk flow
          hydrationGraceTimer = window.setTimeout(() => {
            if (!mounted || initialEventHandled) return;
            setLoading(false);
            isInitialLoad = false;
            console.log('[AuthContext getSession] no session - loading false by grace timer');
              // Rydd opp hard timeout timer hvis den finnes
              if (hardTimeoutTimer) {
                clearTimeout(hardTimeoutTimer);
              }
          }, 500);
          }
        }
      } catch (err) {
        console.error('[AuthContext getSession] error', err);
        if (mounted) setLoading(false);
        isInitialLoad = false;
        console.log('[AuthContext getSession] error - loading false');
        // Rydd opp timere hvis de finnes
        if (hydrationGraceTimer) {
          clearTimeout(hydrationGraceTimer);
          hydrationGraceTimer = undefined;
        }
        if (hardTimeoutTimer) {
          clearTimeout(hardTimeoutTimer);
        }
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext onAuthStateChange]', { event, hasSession: Boolean(session) });
      if (!mounted) return;
      if (event === 'INITIAL_SESSION') {
        initialEventHandled = true;
        // Rydd opp timere hvis de finnes
        if (hydrationGraceTimer) {
          clearTimeout(hydrationGraceTimer);
          hydrationGraceTimer = undefined;
        }
        if (hardTimeoutTimer) {
          clearTimeout(hardTimeoutTimer);
        }
        try {
          if (session) {
            // Sett minimal bruker umiddelbart for å unngå heng
            const minimalUser: User = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.email?.split('@')[0] || 'Bruker',
              apartment: '',
              isAdmin: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            setUser(minimalUser);
            setIsAuthenticated(true);
            console.log('[AuthContext INITIAL_SESSION] minimal user set', { userId: minimalUser.id });
            setAdminResolved(false);
            // Hent full brukerdata i bakgrunnen (best-effort)
            fetchUserData(session.user.id, session.user.email || '').then(full => {
              if (!mounted || !full) return;
              full.email = session.user.email || '';
              setUser(full);
              setAdminResolved(true);
              console.log('[AuthContext INITIAL_SESSION] full user set', { userId: full.id });
            }).catch(() => {});
          } else {
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false); // Sett loading til false umiddelbart når ingen session
            console.log('[AuthContext INITIAL_SESSION] no session');
          }
        } catch {
          if (!mounted) return;
          // Ved feil, sett loading til false
          if (mounted) setLoading(false);
        } finally {
          if (mounted) setLoading(false);
          isInitialLoad = false;
          console.log('[AuthContext INITIAL_SESSION] loading false');
        }
        return;
      }

      if (isInitialLoad && event === 'SIGNED_IN' && session) {
        // Sett minimal bruker umiddelbart; hent full data async
        const minimalUser: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.email?.split('@')[0] || 'Bruker',
          apartment: '',
          isAdmin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setUser(minimalUser);
        setIsAuthenticated(true);
        setError(null);
        console.log('[AuthContext early SIGNED_IN] minimal user set', { userId: minimalUser.id });
        if (mounted) setLoading(false);
        setAdminResolved(false);
        isInitialLoad = false;
        // Best-effort oppgradering til full brukerdata
        fetchUserData(session.user.id, session.user.email || '').then(full => {
          if (!mounted || !full) return;
          full.email = session.user.email || '';
          setUser(full);
          setAdminResolved(true);
          console.log('[AuthContext early SIGNED_IN] full user set', { userId: full.id });
        }).catch(() => {});
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        // Sett minimal bruker umiddelbart; hent full data async
        const minimalUser: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.email?.split('@')[0] || 'Bruker',
          apartment: '',
          isAdmin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setUser(minimalUser);
        setIsAuthenticated(true);
        setError(null);
        setLoading(false);
        setAdminResolved(false);
        console.log('[AuthContext SIGNED_IN] minimal user set', { userId: minimalUser.id });
        // Best-effort oppgradering
        fetchUserData(session.user.id, session.user.email || '').then(full => {
          if (!mounted || !full) return;
          full.email = session.user.email || '';
          setUser(full);
          setAdminResolved(true);
          console.log('[AuthContext SIGNED_IN] full user set', { userId: full.id });
        }).catch(() => {});
      } else if (event === 'SIGNED_OUT') {
        // Hard nullstilling ved utlogging
        try {
          localStorage.removeItem('currentResidence');
          localStorage.removeItem('condo_auth_token');
        } catch {
          // ignorer
        }
        try {
          clearResidenceCache();
        } catch {
          // ignorer
        }
        try {
          queryClient.clear();
        } catch {
          // ignorer
        }
        setUser(null);
        setIsAuthenticated(false);
        setAdminResolved(false);
        console.log('[AuthContext SIGNED_OUT] hard reset completed');
        // Sett loading til false for å ungå heng i spinner etter utlogging
        setLoading(false);
        // Early return slik at vi ikke faller videre i onAuthStateChange
        return;
      } else if (event === 'TOKEN_REFRESHED' && session) {
        try {
          const userData = await fetchUserData(session.user.id, session.user.email || '');
          if (!mounted) return;
          userData.email = session.user.email || '';
          setUser(userData);
          setIsAuthenticated(true);
          setAdminResolved(true);
          console.log('[AuthContext TOKEN_REFRESHED] user refreshed', { userId: userData.id });
        } catch {
          if (!mounted) return;
        }
      }
      setLoading(false);
      console.log('[AuthContext onAuthStateChange] loading false after event');
    });

    return () => {
      mounted = false;
      if (hydrationGraceTimer) {
        clearTimeout(hydrationGraceTimer);
      }
      if (hardTimeoutTimer) {
        clearTimeout(hardTimeoutTimer);
      }
      subscription.unsubscribe();
    };
  }, [fetchUserData, queryClient, optimisticState.isAuthenticated]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    adminResolved,
    clearError,
    refreshUserData,
    refreshAdminStatus,
    forceRefreshUserData,
    loadFullUserData,
    resetPassword,
    updatePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Fail-safe: return minimal default for hot-reload or edge mount timing
    return {
      user: null,
      loading: true,
      error: null,
      isAuthenticated: false,
      login: async () => {
        throw new Error('AuthProvider ikke initialisert');
      },
      logout: async () => {},
      clearError: () => {},
      refreshUserData: async () => {},
      refreshAdminStatus: async () => {},
      forceRefreshUserData: async () => {},
      loadFullUserData: async () => null,
      resetPassword: async () => {
        throw new Error('AuthProvider ikke initialisert');
      },
      updatePassword: async () => {
        throw new Error('AuthProvider ikke initialisert');
      }
    } as const;
  }
  return context;
};